import { Num, T, useGT, Var } from 'gt-react';
import type { WorkbookRunTime } from './workbookRunTime';
import type { SheetPreview } from './SheetEditor';

/**
 * The Live preview's label: when its inputs were read, and which Sources it read now
 * instead of reusing the Base run's. All reused: as of the Base run. All fresh: as of
 * the read. Mixed: as of the Base run, with the fresh Sources named.
 */
export function LivePreviewLabel({
  preview,
  inputsAsOf,
  runTime,
}: {
  preview: SheetPreview;
  inputsAsOf: string;
  runTime: WorkbookRunTime;
}) {
  const gt = useGT();
  const [firstFresh] = preview.freshSources;
  const allFresh = !!firstFresh && preview.reusedSourceCount === 0;
  const asOf = allFresh ? firstFresh.readAt : inputsAsOf;
  const mixed = !!firstFresh && !allFresh;
  const freshNames =
    preview.freshSources.length === 1
      ? firstFresh?.name
      : gt('{count} sources', {
          context:
            'worksheet live preview label, how many sources were read now',
          count: String(preview.freshSources.length),
        });
  return (
    <span
      role="status"
      className="shrink-0 rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground"
      title={runTime.full(asOf)}
    >
      {mixed ? (
        <T context="worksheet live preview label, some sources read now">
          Preview · inputs as of <Var>{runTime.short(asOf)}</Var>,{' '}
          <Var>{freshNames}</Var> read now
        </T>
      ) : (
        <T context="worksheet live preview label">
          Preview · inputs as of <Var>{runTime.short(asOf)}</Var>
        </T>
      )}
      {preview.pending && '…'}
    </span>
  );
}

/**
 * A first look's label: the preview read only each Source's first rows, since
 * no Run holds its inputs yet, so a Run is what computes the real Result.
 */
export function FirstLookLabel({
  preview,
  headRows,
}: {
  preview: SheetPreview;
  headRows: number;
}) {
  const gt = useGT();
  return (
    <span
      role="status"
      className="shrink-0 rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground"
      title={gt(
        'Computed over only the first {rows} rows of each data source. Run the workbook for the full result.',
        {
          rows: String(headRows),
          context: 'worksheet first look preview label tooltip',
        },
      )}
    >
      <T context="worksheet first look preview label">
        Preview · first <Num>{headRows}</Num> rows of each source
      </T>
      {preview.pending && '…'}
    </span>
  );
}
